import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useModalStore } from '@/store';

/**
 * Dropzone — a delightful, dashed-border, drag-and-drop file picker shell.
 *
 * Used by every file/image/audio/video field renderer in the tool form so they
 * share a single visual language and a single drag-and-drop implementation
 * (no duplicated event wiring, no theming drift).
 *
 * The body always exposes two explicit affordances:
 *   • "Upload" — opens the OS file picker (or fires the hidden web input).
 *   • "Library" — opens FilesModal so users can pick previously uploaded files.
 *
 * Props:
 *   accept            HTML accept string passed to the hidden <input> on web.
 *   multiple          true → allow selecting multiple files at once.
 *   disabled          dim + ignore interactions.
 *   isUploading       show a spinner inside the zone.
 *   isFull            when true, disable picking and dim the CTA (e.g. multi
 *                     uploads at their cap).
 *   primaryLabel      headline copy.
 *   secondaryLabel    helper copy.
 *   icon              FontAwesome icon name (defaults to cloud-arrow-up).
 *   compact           render the slim variant for use inline next to previews.
 *   onFiles(files[])  called with raw File-like objects from picker or drop.
 *   onLibrary(files[]) called with already-uploaded library file objects when
 *                     the user picks from FilesModal. If omitted, the Library
 *                     button is hidden.
 *   children          optional node rendered between the icon bubble and the
 *                     primary label.
 */
export default function Dropzone({
  accept,
  multiple = false,
  disabled = false,
  isUploading = false,
  isFull = false,
  primaryLabel = 'Drag and drop a file here',
  secondaryLabel,
  icon = 'cloud-arrow-up',
  compact = false,
  onFiles,
  onLibrary,
  children,
}) {
  const { colors } = useTheme();
  const inputRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const openModal = useModalStore(state => state.actions.openModal);

  const interactive = !disabled && !isUploading && !isFull;

  // Drag-counter: child elements firing dragenter/dragleave would otherwise
  // flicker the overlay. We only flip `isDragging` when the counter hits 0.
  const dragCounterRef = useRef(0);

  useEffect(() => {
    if (Platform.OS !== 'web' || !interactive) return undefined;
    // Suppress the browser's "open file" default when a user drops outside
    // the zone — feels broken if a misaimed drop replaces the page.
    const prevent = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, [interactive]);

  const openPicker = useCallback(async () => {
    if (!interactive) return;
    if (Platform.OS === 'web') {
      inputRef.current?.click?.();
      return;
    }
    try {
      // On iOS, media accepts get an action sheet with a photo-library
      // option; the document picker alone can't browse the photo library.
      const { pickNativeFiles } = await import('@/constants/utils/nativeMediaPicker');
      const files = await pickNativeFiles({ accept, multiple });
      if (files?.length) onFiles?.(files);
    } catch {
      // Surface upstream — Dropzone itself is purely UI.
    }
  }, [accept, interactive, multiple, onFiles]);

  // Map an HTML `accept` string to the backend's coarse fileType filter so
  // FilesModal only surfaces files that match this dropzone's purpose.
  // Falls back to `null` (show all) if accept is wide-open or unrecognised.
  const libraryFileType = useMemo(() => {
    const a = (accept || '').toLowerCase();
    if (!a || a === '*/*') return null;
    if (a.includes('image/')) return 'image';
    if (a.includes('audio/')) return 'audio';
    if (a.includes('video/')) return 'video';
    if (a.includes('pdf') || a.includes('text/')) return 'document';
    return null;
  }, [accept]);

  const libraryTitle = useMemo(() => {
    switch (libraryFileType) {
      case 'image': return 'Choose from your images';
      case 'audio': return 'Choose from your audio files';
      case 'video': return 'Choose from your videos';
      case 'document': return 'Choose from your documents';
      default: return 'Choose from your library';
    }
  }, [libraryFileType]);

  const openLibrary = useCallback(() => {
    if (!interactive || !onLibrary) return;
    openModal('FilesModal', {
      title: libraryTitle,
      fileType: libraryFileType,
      onSelect: (picked) => {
        const list = Array.isArray(picked) ? picked : (picked ? [picked] : []);
        if (!list.length) return;
        onLibrary(multiple ? list : [list[0]]);
      },
    });
  }, [interactive, multiple, onLibrary, openModal, libraryFileType, libraryTitle]);

  // ---------------------------------------------------------------------------
  // Web drag-and-drop wiring (RN web ignores these events on native bundles).
  // ---------------------------------------------------------------------------
  const webHandlers = Platform.OS === 'web' && interactive ? {
    onDragEnter: (e) => {
      e.preventDefault();
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setIsDragging(true);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) setIsDragging(false);
    },
    onDragOver: (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    },
    onDrop: (e) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const list = Array.from(e.dataTransfer?.files || []);
      if (!list.length) return;
      onFiles?.(multiple ? list : [list[0]]);
    },
  } : null;

  const styles = useMemo(() => StyleSheet.create({
    zone: {
      width: '100%',
      borderRadius: 14,
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 16,
      paddingVertical: compact ? 16 : 26,
      gap: compact ? 8 : 12,
      ...(Platform.OS === 'web' && {
        transition: 'border-color 160ms ease, background-color 160ms ease',
      }),
    },
    zoneHover: {
      borderColor: colors.border_color_primary,
      backgroundColor: colors.solid_color_tertiary,
    },
    zoneDragging: {
      borderColor: colors.text_color_primary,
      backgroundColor: colors.solid_color_tertiary,
    },
    zoneDisabled: { opacity: 0.6 },

    iconBubble: {
      width: compact ? 32 : 44,
      height: compact ? 32 : 44,
      borderRadius: 999,
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
    },
    primary: { textAlign: 'center' },
    secondary: { textAlign: 'center' },

    actionsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 8,
    },
    btn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
      borderWidth: 1,
    },
    btnPrimary: {
      borderColor: colors.text_color_primary,
      backgroundColor: colors.text_color_primary,
    },
    btnSecondary: {
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
    },
    btnSecondaryHover: {
      borderColor: colors.text_color_primary,
    },
    btnTextOnPrimary: { color: colors.solid_color_primary },

    overlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.04)',
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
    },
    overlayPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 999,
      backgroundColor: colors.text_color_primary,
    },
  }), [colors, compact]);

  const accentActive = isHovered || isDragging;
  const showLibrary = !!onLibrary && Platform.OS === 'web';

  return (
    <View {...(webHandlers || {})}>
      <Pressable
        onPress={openPicker}
        disabled={!interactive}
        onHoverIn={() => Platform.OS === 'web' && setIsHovered(true)}
        onHoverOut={() => Platform.OS === 'web' && setIsHovered(false)}
        style={[
          styles.zone,
          accentActive && styles.zoneHover,
          isDragging && styles.zoneDragging,
          (disabled || isFull) && styles.zoneDisabled,
        ]}
      >
        <View style={styles.iconBubble}>
          {isUploading ? (
            <ActivityIndicator size='small' color={colors.text_color_secondary} />
          ) : (
            <Icon
              name={icon}
              size={compact ? 14 : 18}
              color={accentActive ? colors.text_color_primary : colors.text_color_secondary}
            />
          )}
        </View>

        {children}

        <Text size={compact ? 's' : 'n'} weight='bold' color='primary' style={styles.primary}>
          {isUploading ? 'Uploading…' : primaryLabel}
        </Text>

        {secondaryLabel ? (
          <Text size='xs' color='secondary' style={styles.secondary}>{secondaryLabel}</Text>
        ) : null}

        {/*
          Explicit action buttons replace the old faint nested "browse" text.
          On native we still rely on the whole zone being tappable + the OS
          file picker; on web we surface separate Upload / Library affordances
          since the zone-press path can be missed (no hover affordance on
          touch devices).
        */}
        <View style={styles.actionsRow}>
          <Pressable
            onPress={openPicker}
            disabled={!interactive}
            onHoverIn={() => Platform.OS === 'web' && setIsHovered(true)}
            onHoverOut={() => Platform.OS === 'web' && setIsHovered(false)}
            style={[styles.btn, styles.btnPrimary]}
          >
            <Icon name='arrow-up-from-bracket' size={11} color={colors.solid_color_primary} />
            <Text size='s' weight='bold' style={styles.btnTextOnPrimary}>Upload</Text>
          </Pressable>

          {showLibrary && (
            <Pressable
              onPress={openLibrary}
              disabled={!interactive}
              style={({ hovered }) => [
                styles.btn,
                styles.btnSecondary,
                hovered && styles.btnSecondaryHover,
              ]}
            >
              <Icon name='folder-open' size={11} color={colors.text_color_primary} />
              <Text size='s' weight='bold'>Library</Text>
            </Pressable>
          )}
        </View>

        {isDragging && (
          <View style={styles.overlay} pointerEvents='none'>
            <View style={styles.overlayPill}>
              <Icon name='cloud-arrow-down' size={14} color={'#ffffff'} />
              <Text size='s' weight='bold' style={{ color: '#ffffff' }}>Release to upload</Text>
            </View>
          </View>
        )}
      </Pressable>

      {Platform.OS === 'web' && (
        // eslint-disable-next-line react/forbid-elements
        <input
          ref={inputRef}
          type='file'
          accept={accept || undefined}
          multiple={!!multiple}
          onChange={(e) => {
            const list = Array.from(e.target.files || []);
            if (!list.length) return;
            onFiles?.(multiple ? list : [list[0]]);
            e.target.value = '';
          }}
          style={{ display: 'none' }}
        />
      )}
    </View>
  );
}
